import { useMemo, useState } from 'react';
import {
    Box,
    Button,
    FormControlLabel,
    Skeleton,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { NO_MIN_TARGET, keepGroupHeight } from '../../../../root/touchTheme';
import { eventKey } from '../model/fromAssetDetail';
import type { CandidateEvent } from '../model/types';
import { CANDIDATE_GRID, CandidateEventRow } from './CandidateEventRow';
import {
    sortCandidates,
    visibleCandidates,
    type CandidateSort,
} from './sortCandidates';

export const PAGE_SIZE = 20;

export function closedLabel(
    loaded: boolean,
    loading: boolean,
    count: number
): string {
    if (loading) return 'Closed (loading)';
    return loaded ? `Closed (${count})` : 'Closed';
}

interface CandidateEventListProps {
    events: readonly CandidateEvent[];
    loading: boolean;
    error: boolean;
    selectedKeys: readonly string[];
    maxSelected: number;
    colors: ReadonlyMap<string, string>;
    onToggle: (key: string) => void;
    onClear: () => void;
    showClosed: boolean;
    onShowClosedChange: (show: boolean) => void;
    closedLoaded: boolean;
    closedLoading: boolean;
}

function HeadCell({
    children,
    align = 'right',
    title,
}: {
    children: React.ReactNode;
    align?: 'left' | 'right';
    title?: string;
}) {
    return (
        <Typography
            title={title}
            sx={{
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'text.disabled',
                textAlign: align,
            }}
        >
            {children}
        </Typography>
    );
}

export function CandidateEventList({
    events,
    loading,
    error,
    selectedKeys,
    maxSelected,
    colors,
    onToggle,
    onClear,
    showClosed,
    onShowClosedChange,
    closedLoaded,
    closedLoading,
}: CandidateEventListProps) {
    const [sort, setSort] = useState<CandidateSort>('match');
    const [limit, setLimit] = useState(PAGE_SIZE);
    const selected = useMemo(() => new Set(selectedKeys), [selectedKeys]);
    const closedCount = events.filter(e => !e.open).length;

    const rows = useMemo(
        () =>
            sortCandidates(
                visibleCandidates(events, showClosed, selected, eventKey),
                sort
            ),
        [events, showClosed, selected, sort]
    );
    const shown = rows.slice(0, limit);
    const canSelectMore = selectedKeys.length < maxSelected;

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5,
                }}
            >
                <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                    Candidate events
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    {rows.length}
                </Typography>
                {selectedKeys.length > 0 && (
                    <Button
                        size="small"
                        onClick={onClear}
                        sx={{ fontSize: '11px', textTransform: 'none', py: 0 }}
                    >
                        Clear selection
                    </Button>
                )}
                <Box sx={{ flex: 1 }} />
                {(!closedLoaded || closedCount > 0) && (
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={showClosed}
                                onChange={e =>
                                    onShowClosedChange(e.target.checked)
                                }
                            />
                        }
                        label={closedLabel(
                            closedLoaded,
                            closedLoading,
                            closedCount
                        )}
                        sx={{
                            mr: 0,
                            '& .MuiFormControlLabel-label': {
                                fontSize: '11px',
                                color: 'text.secondary',
                            },
                        }}
                    />
                )}
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={sort}
                    onChange={(_, value: CandidateSort | null) => {
                        if (value) setSort(value);
                    }}
                    sx={{
                        ...keepGroupHeight(24),
                        '& .MuiToggleButton-root': {
                            py: 0,
                            px: 1,
                            fontSize: '11px',
                            textTransform: 'none',
                            // a 24 px row: keep its size on touch screens
                            ...NO_MIN_TARGET,
                        },
                    }}
                >
                    <ToggleButton value="match">Match</ToggleButton>
                    <ToggleButton value="volume">Volume</ToggleButton>
                    <ToggleButton value="end">End date</ToggleButton>
                </ToggleButtonGroup>
            </Box>
            <Typography
                sx={{ fontSize: '11px', color: 'text.secondary', mb: 0.5 }}
            >
                Matched by text similarity. Not a measured effect. Click a row
                to compare its probability with the price.
            </Typography>

            {loading && events.length === 0 ? (
                <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                >
                    {[0, 1, 2, 3].map(i => (
                        <Skeleton key={i} variant="rounded" height={32} />
                    ))}
                </Box>
            ) : error && events.length === 0 ? (
                <Typography
                    sx={{ fontSize: '12px', color: 'error.main', py: 1 }}
                >
                    The candidate events could not be loaded.
                </Typography>
            ) : rows.length === 0 ? (
                <Typography
                    sx={{ fontSize: '12px', color: 'text.secondary', py: 1 }}
                >
                    {(!closedLoaded || closedCount > 0) && !showClosed
                        ? 'No open candidate events. Switch on "Closed" to see closed ones.'
                        : 'No candidate events found for this asset.'}
                </Typography>
            ) : (
                <>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: CANDIDATE_GRID,
                            columnGap: 2,
                            px: 1.5,
                            py: 0.5,
                        }}
                    >
                        <HeadCell align="left">Event</HeadCell>
                        <HeadCell title="Probability of the leading outcome">
                            Leading outcome
                        </HeadCell>
                        <HeadCell
                            align="left"
                            title="Latest net capital of the informed and whale wallets"
                        >
                            Flow
                        </HeadCell>
                        <HeadCell>Volume</HeadCell>
                        <HeadCell>End</HeadCell>
                        <HeadCell title="Text similarity score. Not a measured effect.">
                            Match
                        </HeadCell>
                        <span />
                    </Box>
                    {shown.map(event => {
                        const key = eventKey(event);
                        return (
                            <CandidateEventRow
                                key={key}
                                event={event}
                                color={colors.get(key) ?? null}
                                selected={selected.has(key)}
                                selectable={canSelectMore}
                                onToggle={() => onToggle(key)}
                            />
                        );
                    })}
                    {rows.length > shown.length && (
                        <Button
                            size="small"
                            onClick={() => setLimit(l => l + PAGE_SIZE)}
                            sx={{
                                fontSize: '11px',
                                textTransform: 'none',
                                ml: 0.5,
                                mt: 0.5,
                            }}
                        >
                            Show{' '}
                            {Math.min(PAGE_SIZE, rows.length - shown.length)}{' '}
                            more
                        </Button>
                    )}
                </>
            )}
        </Box>
    );
}
